import { getAllOrgUsers, inviteUser, inviteUserByEmail } from "@/api/users"
import { allRolesItemKey, inviteRolesKeys, rolesKeys } from "@/constants/role"
import { ITeamMember, IUpdateUserForm } from "@/types/user.types"
import { formatFullName, splitFullName } from "@/utils/formatFullName"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faMagnifyingGlass, faPlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Input, Select, message } from "antd"
import { useTranslation } from "react-i18next"
import { useEffect, useState } from "react"

import CreateUserModal from "./CreateUserModal"
import OrgUsersTable from "./OrgUsersTable"
import { Loader } from "@/UI"
import SettingSection from "../../../shared/SettingSection"
import SettingContentWrapper from "../../../shared/SettingContentWrapper"
import { isManagerSelector, userAtom } from "@/atoms/userAtom"
import { useRecoilValue } from "recoil"
import KosmoButton from "@/UI/Buttons/KosmoButton"

const { Option } = Select

interface OrgUsersSection {}

const roleFilters = [allRolesItemKey].concat(rolesKeys)

const OrgUsersSection: React.FC<OrgUsersSection> = ({}) => {
  const { t } = useTranslation()

  const user = useRecoilValue(userAtom)
  const isManager = useRecoilValue(isManagerSelector)

  const [members, setMembers] = useState<ITeamMember[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [filteredMembers, setFilteredMembers] = useState<ITeamMember[]>([])
  const [filter, setFilter] = useState<{ role: string; input: string }>({
    input: "",
    role: allRolesItemKey.value,
  })

  const handleInputFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({ ...filter, input: event.target.value })
  }

  const handleRoleFilter = (value: string) => {
    setFilter({ ...filter, role: value })
  }

  const getOrgMembers = () => {
    setIsLoading(true)
    return getAllOrgUsers()
      .then(({ data }) => {
        const formattedInvitations =
          data.pendingInvitations?.map((invitee) => {
            const { firstName, lastName } = splitFullName(invitee.name)
            return {
              id: "",
              invitationId: invitee.id,
              firstName,
              lastName,
              email: invitee.email,
              phone: invitee.phone,
              role: invitee.role,
              // locationId: invitee.locationId,
              invitationLink: invitee.invitationLink,
            }
          }) || []
        setMembers([...data.users, ...formattedInvitations])
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => setIsLoading(false))
  }

  const submitInviteMember = async (invitee: IUpdateUserForm): Promise<boolean> => {
    try {
      if (!invitee.email && !invitee.phone) {
        message.error(t("profile.TeamPeople.phoneOrEmailRequired"))
        return false
      }

      if (invitee.email) {
        await inviteUserByEmail({
          name: invitee.name,
          email: invitee.email?.toLowerCase(),
          role: invitee.role,
          // locationId: invitee.locationId,
        })
      } else if (invitee.phone) {
        await inviteUser({
          name: invitee.name,
          phone: invitee.phone,
          role: invitee.role,
          // locationId: invitee.locationId,
        })
      }
      message.success(t("profile.TeamPeople.inviteSentNotifSuccess"))
      await getOrgMembers()
      return true
    } catch (error) {
      message.error(t("profile.TeamPeople.inviteSentNotifFailure"))
      return false
    }
  }

  useEffect(() => {
    setFilteredMembers(
      members.filter((item) => {
        const name = formatFullName(item.firstName, item.lastName)
        // role valid when `filter.role is all` or `item.role is equal to filter.role`
        const isRoleValid = filter.role === allRolesItemKey.value || item.role === filter.role
        // name/phone valid when `filter.input is empty` or `name/phone contains filter.input`
        const isNameValid =
          name.toLowerCase().includes(filter.input.trim().toLowerCase()) ||
          item?.phone?.toLowerCase().includes(filter.input.trim().toLowerCase())
        return isRoleValid && isNameValid
      })
    )
  }, [filter, members])

  //Reset filters when members change
  useEffect(() => {
    setFilter({ input: "", role: allRolesItemKey.value })
  }, [members])

  useEffect(() => {
    getOrgMembers()
  }, [user?.org?.id])

  return (
    <SettingSection title={t("profile.TeamPeople.people")}>
      <SettingContentWrapper className="gap-y-3">
        <div className="flex settings-filter xs:flex-wrap gap-x-3 gap-y-2">
          <Input
            placeholder={t("invitation.TeamPoeple.searchByNamePhone")}
            className="font-normal rounded-md xs:mb-3 xs:mr-0"
            prefix={
              <FontAwesomeIcon
                icon={faMagnifyingGlass as IconProp}
                className="mr-1 text-gray-400"
              />
            }
            value={filter.input}
            onChange={handleInputFilter}
          />
          <Select value={filter.role} className="text-sm xs:ml-auto" onChange={handleRoleFilter}>
            {roleFilters.map((role, i) => (
              <Option value={role.value} key={i}>
                {t(role.title)}
              </Option>
            ))}
          </Select>
          {!isManager && (
            <CreateUserModal
              title={t("invitation.TeamPeople.invitePeople")}
              button={
                <KosmoButton id="invite-member-button" type="primary">
                  <div className="flex items-center gap-x-2">
                    <FontAwesomeIcon icon={faPlus} color="#FFF" />
                    <p>{t("invitation.TeamPeople.invitePeople")}</p>
                  </div>
                </KosmoButton>
              }
              isCancelButton={false}
              confirmButtonText={t("invitation.TeamPeople.sendInvite")}
              onConfirm={submitInviteMember}
              roles={inviteRolesKeys}
            />
          )}
        </div>
        {isLoading ? (
          <Loader fontSize={48} />
        ) : (
          <OrgUsersTable
            data={filteredMembers}
            getOrgMembers={getOrgMembers}
            isLoading={isLoading}
          />
        )}
      </SettingContentWrapper>
    </SettingSection>
  )
}

export default OrgUsersSection
